import { useFlaggingCallback } from '@databases/flags';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import {
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseValue } from '@databases/types/database';
import { DatabaseViewSettings } from '@databases/types/database/view';
import {
  DatabaseChange,
  DatabaseChangeType,
  ValueChange,
} from '@databases/utils/database/changesets';
import { getInitialValue } from '@databases/utils/database/field-types';
import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
} from '@databases/utils/database/utils';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { batch } from '@databases/utils/redux/redux';
import React, { forwardRef } from 'react';

import { InternalDatabaseValueEdit } from './DatabaseValueEdit';

type DatabaseValueBulkEditProps = {
  contentId: string;
  scope: string;
  viewSettings: DatabaseViewSettings | undefined;
  value: DatabaseValue | undefined;
  onBulkEditChange: (value: DatabaseValue, change: ValueChange | null) => void;
};

const DatabaseValueBulkEdit = forwardRef<HTMLInputElement, DatabaseValueBulkEditProps>(
  function DatabaseValueBulkEdit(
    { contentId, scope, viewSettings, value, onBulkEditChange },
    ref
  ) {
    const [firstSelectedField, selectedEntryIds, firstSelectedEntry] =
      defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
        const database = selectDatabaseByContentIdRequired(state, {
          contentId,
        });
        const selectedFieldIds = selectSelectedFieldIds(state, {
          contentId,
          scope,
          viewSettings,
        });
        const selectedEntryIds = selectSelectedEntryIds(state, {
          contentId,
          scope,
          viewSettings,
        });
        const firstSelectedField = getDatabaseFieldOptional(
          database,
          selectedFieldIds[0]
        );
        const firstSelectedEntry = getDatabaseEntryOptional(
          database,
          selectedEntryIds[0]
        );

        return [firstSelectedField, selectedEntryIds, firstSelectedEntry] as const;
      });

    const dispatch = defaultStoreUtils.useDispatch();
    const handleAddChangeToDraft = useFlaggingCallback(
      (change: DatabaseChange) => {
        dispatch(addChangeToDraft(contentId, scope, change));
      },
      [contentId, dispatch, scope]
    );

    const store = defaultStoreUtils.useStore();
    const handleBulkEditValues = useFlaggingCallback(
      (value: DatabaseValue, change: ValueChange | null) => {
        if (!firstSelectedField) {
          return;
        }
        onBulkEditChange(value, change);
        batch(() => {
          collabDatabaseBatch(store, contentId, () => {
            selectedEntryIds.forEach((entryId) => {
              handleAddChangeToDraft({
                type: DatabaseChangeType.SET_VALUE,
                fieldId: firstSelectedField.id,
                entryId: entryId,
                value: value,
                change,
              });
            });
          });
        });
      },
      [
        contentId,
        handleAddChangeToDraft,
        onBulkEditChange,
        selectedEntryIds,
        firstSelectedField,
        store,
      ]
    );

    if (!firstSelectedField) {
      return null;
    }

    return (
      <InternalDatabaseValueEdit
        contentId={contentId}
        field={firstSelectedField}
        entryId={firstSelectedEntry?.id}
        value={value || getInitialValue(firstSelectedField.type)}
        isSeamless
        onChange={handleBulkEditValues}
        autoFocus
        addChangeToDraft={handleAddChangeToDraft}
        ref={ref}
        isBulkEdit={true}
      />
    );
  }
);

export default DatabaseValueBulkEdit;
